import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Plus, Check, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

interface Habit {
  id: string;
  title: string;
  completed_today: boolean;
  streak: number;
}

const DashboardHabits = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [habits, setHabits] = useState<Habit[]>([]);
  const [newHabit, setNewHabit] = useState("");

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/auth");
      return;
    }
    loadHabits();
  };

  const loadHabits = () => {
    const savedHabits = localStorage.getItem("user_habits");
    if (savedHabits) {
      const parsed = JSON.parse(savedHabits);
      const today = new Date().toDateString();
      const lastCheck = localStorage.getItem("habits_last_check");
      
      if (lastCheck !== today) {
        // Reset daily completion
        const reset = parsed.map((h: Habit) => ({ ...h, completed_today: false }));
        setHabits(reset);
        localStorage.setItem("user_habits", JSON.stringify(reset));
        localStorage.setItem("habits_last_check", today);
      } else {
        setHabits(parsed);
      }
    }
    setLoading(false);
  };

  const saveHabits = (updatedHabits: Habit[]) => {
    localStorage.setItem("user_habits", JSON.stringify(updatedHabits));
    setHabits(updatedHabits);
  };

  const addHabit = () => {
    if (!newHabit.trim()) return;
    
    const habit: Habit = {
      id: crypto.randomUUID(),
      title: newHabit,
      completed_today: false,
      streak: 0
    };
    
    saveHabits([...habits, habit]);
    setNewHabit("");
    toast({ title: "Habit added successfully!" });
  };

  const toggleHabit = (id: string) => {
    const updated = habits.map(h => {
      if (h.id === id) {
        const completed = !h.completed_today;
        return {
          ...h,
          completed_today: completed,
          streak: completed ? h.streak + 1 : Math.max(0, h.streak - 1)
        };
      }
      return h;
    });
    saveHabits(updated);
  };

  const deleteHabit = (id: string) => {
    saveHabits(habits.filter(h => h.id !== id));
    toast({ title: "Habit removed" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const completedToday = habits.filter(h => h.completed_today).length;
  const totalHabits = habits.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        <div className="space-y-2">
          <h1 className="text-4xl font-bold">Daily Habits</h1>
          <p className="text-muted-foreground">Build consistency with daily check-ins</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Today's Progress</CardTitle>
            <CardDescription>
              {completedToday} of {totalHabits} habits completed
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="w-full bg-secondary rounded-full h-3">
              <div
                className="bg-primary h-3 rounded-full transition-all duration-500"
                style={{ width: `${totalHabits > 0 ? (completedToday / totalHabits) * 100 : 0}%` }}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Add New Habit</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                placeholder="e.g., Read for 30 minutes"
                value={newHabit}
                onChange={(e) => setNewHabit(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && addHabit()}
              />
              <Button onClick={addHabit}>
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-3">
          {habits.map((habit) => (
            <Card key={habit.id} className="hover-scale">
              <CardContent className="flex items-center justify-between p-4">
                <div className="flex items-center gap-4 flex-1">
                  <Checkbox
                    checked={habit.completed_today}
                    onCheckedChange={() => toggleHabit(habit.id)}
                  />
                  <div className="flex-1">
                    <p className={`font-medium ${habit.completed_today ? 'line-through text-muted-foreground' : ''}`}>
                      {habit.title}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      🔥 {habit.streak} day streak
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => deleteHabit(habit.id)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}

          {habits.length === 0 && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-muted-foreground mb-2">No habits yet</p>
                <p className="text-sm text-muted-foreground">Add your first habit to get started!</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardHabits;
